import { Settings2 } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Data, DataField, DataFieldType } from "schema/agent";
import { useAnimation } from "@/features/animation";
import { useProject } from "@/features/project";
import { AssetField } from "./AssetField";
import { BooleanField } from "./BooleanField";
import { ColorField } from "./ColorField";
import { DimensionsPanel } from "./DimensionsPanel";
import { NumberField } from "./NumberField";
import { SelectField } from "./SelectField";
import { TextareaField } from "./TextareaField";
import { TextField } from "./TextField";

const fieldComponents: Record<DataFieldType, React.FC<{ field: never; onChange: never }>> = {
  text: TextField,
  textarea: TextareaField,
  number: NumberField,
  boolean: BooleanField,
  select: SelectField,
  color: ColorField,
  asset: AssetField,
};

interface DataPanelProps {
  chatPanelRef: React.RefObject<HTMLDivElement | null>;
}

export const DataPanel: React.FC<DataPanelProps> = ({ chatPanelRef }) => {
  const { data, updateProject } = useProject();
  const { controller } = useAnimation();
  const [isOpen, setIsOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const prevDataRef = useRef<Data | null | undefined>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  // Subscribe to animation controller for current time
  useEffect(() => {
    const unsubscribe = controller.subscribe((state) => {
      setCurrentTime(state.currentTimeMs / 1000);
    });
    return unsubscribe;
  }, [controller]);

  // Auto-open when data updates
  useEffect(() => {
    if (prevDataRef.current !== data && Object.keys(data).length > 0) {
      // Only auto-open if this is a new data update (not initial load)
      if (prevDataRef.current !== null) {
        setIsOpen(true);
      }
    }
    prevDataRef.current = data;
  }, [data]);

  // Calculate panel position based on button relative to sidebar container
  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current || !chatPanelRef.current) {
      return;
    }

    const chatPanelRect = chatPanelRef.current.getBoundingClientRect();
    const buttonRect = buttonRef.current.getBoundingClientRect();
    setPanelStyle({
      bottom: chatPanelRect.bottom - buttonRect.top + 8,
      right: chatPanelRect.right - buttonRect.right,
    });
  }, [isOpen, chatPanelRef.current]);

  const handleFieldChange = useCallback(
    (key: string, value: DataField["value"]) => {
      const updatedField = { ...data[key], value } as DataField;
      const updatedData: Data = {
        ...data,
        [key]: updatedField,
      };
      updateProject({ data: updatedData });
    },
    [data, updateProject],
  );

  const allFields = data ? Object.entries(data) : [];

  // Filter fields based on current time (unless showAll is true)
  const currentFields = allFields.filter(([_, field]) => {
    if (!field.visibleAt) return true;
    return currentTime >= field.visibleAt.start && currentTime <= field.visibleAt.end;
  });

  const visibleFields = showAll ? allFields : currentFields;

  // Check if any field has visibleAt (to show toggle)
  const hasTimeBasedFields = allFields.some(([_, field]) => field.visibleAt);
  const hasData = allFields.length > 0;

  return (
    <>
      {/* Panels - rendered via portal to sidebar for proper z-index */}
      {isOpen &&
        chatPanelRef.current &&
        createPortal(
          <div
            className="absolute grid gap-2 w-fit max-w-[calc(100vw-2rem)] justify-items-end"
            style={panelStyle}
          >
            {/* Dimensions Panel - always shown */}
            <DimensionsPanel />

            {/* Data Fields Panel - only shown when hasData */}
            {hasData && (
              <div className="min-w-64 max-w-full border border-slate-700/50 bg-slate-800/80 backdrop-blur rounded-sm overflow-hidden">
                {/* Tab Header - only shown when hasTimeBasedFields */}
                {hasTimeBasedFields && (
                  <div className="flex border-b border-slate-700/50">
                    <button
                      type="button"
                      onClick={() => setShowAll(false)}
                      className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                        !showAll
                          ? "text-white border-b-2 border-indigo-500"
                          : "text-slate-400 hover:text-slate-200"
                      }`}
                    >
                      Current ({currentFields.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAll(true)}
                      className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                        showAll
                          ? "text-white border-b-2 border-indigo-500"
                          : "text-slate-400 hover:text-slate-200"
                      }`}
                    >
                      All ({allFields.length})
                    </button>
                  </div>
                )}

                {/* Content - auto height with max */}
                <div className="p-2 space-y-5 md:space-y-4 max-h-[min(16rem,calc(100*var(--vh)-15rem))] overflow-y-auto">
                  {visibleFields.length === 0 ? (
                    <p className="text-[10px] text-slate-500 text-center py-2">
                      No editable fields at current time
                    </p>
                  ) : (
                    visibleFields.map(([key, field]) => {
                      const Component = fieldComponents[field.type];
                      if (!Component) return null;
                      return (
                        <Component
                          key={key}
                          field={field as never}
                          onChange={
                            ((value: DataField["value"]) => handleFieldChange(key, value)) as never
                          }
                        />
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>,
          chatPanelRef.current,
        )}

      {/* Button - stays in document flow */}
      <div className="flex justify-end mb-2">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg transition-all border ${
            isOpen
              ? "bg-indigo-600 text-white border-indigo-600"
              : "bg-slate-800 text-slate-300 hover:bg-slate-700 border-slate-700"
          }`}
        >
          <Settings2 size={14} />
          <span className="text-xs font-medium">Customize</span>
          {hasData && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                isOpen ? "bg-indigo-500" : "bg-slate-700"
              }`}
            >
              {allFields.length}
            </span>
          )}
        </button>
      </div>
    </>
  );
};
